import React, { useMemo, useState, useEffect } from 'react'
import { Button, App } from 'antd'
import { useNavigate } from 'react-router-dom'
import { useGwGoodsUpdateQuery, useGwGoodsGuigeListQuery } from '../store/ReactWebSiteApi'
import styles from './WebGoodsSubTypeGoodsDesc.module.css'

const IMG_BASE = '../api/assets/images/application/'

function parseImageFiles(item) {
    const raw = item?.eowg_image_file
    if (raw == null) return []
    if (Array.isArray(raw)) {
        return raw.map((s) => String(s).trim()).filter(Boolean)
    }
    return String(raw)
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean)
}

function toImgSrc(fileName) {
    if (!fileName) return ''
    return `${IMG_BASE}${fileName}`
}

const MAX_THUMBS = 5

function WebGoodsSubTypeGoodsDesc(props) {
    const { item } = props
    const { message } = App.useApp()
    const navigate = useNavigate()
    const [activeIndex, setActiveIndex] = useState(0)
    const [selectedGuige, setSelectedGuige] = useState(null)

    const { data: detailData, isSuccess: detailOk } = useGwGoodsUpdateQuery({ eowg_id: item?.eowg_id }, { skip: !item?.eowg_id })

    const { data: guigeData, isSuccess: guigeOk } = useGwGoodsGuigeListQuery({ eowgg_eowg_id: item?.eowg_id }, { skip: !item?.eowg_id })

    const merged = useMemo(() => {
        if (!item) return null
        if (detailOk && detailData?.Status === 'success' && detailData?.Result) {
            return { ...item, ...detailData.Result }
        }
        return item
    }, [item, detailOk, detailData])

    const fileNames = useMemo(() => parseImageFiles(merged), [merged])
    const urls = useMemo(() => fileNames.map(toImgSrc), [fileNames])
    const thumbFiles = useMemo(() => fileNames.slice(0, MAX_THUMBS), [fileNames])

    useEffect(() => {
        setActiveIndex(0)
    }, [merged?.eowg_id])

    useEffect(() => {
        if (activeIndex >= urls.length && urls.length > 0) {
            setActiveIndex(0)
        }
    }, [urls.length, activeIndex])

    const guigeList = useMemo(() => {
        if (!guigeOk || guigeData?.Status !== 'success' || !Array.isArray(guigeData?.Result)) {
            return []
        }
        return guigeData.Result
    }, [guigeOk, guigeData])

    useEffect(() => {
        if (guigeList.length === 0) {
            setSelectedGuige(null)
            return
        }
        setSelectedGuige((prev) => {
            if (prev && guigeList.some((g) => g.eowgg_id === prev.eowgg_id)) {
                return prev
            }
            return guigeList[0]
        })
    }, [guigeList, merged?.eowg_id])

    const detailHtml = merged?.eowg_content
    const processedDetailHtml = useMemo(() => {
        if (typeof detailHtml !== 'string') return ''
        return detailHtml.replace(/<img([^>]*)data-href="([^"]*)"([^>]*)\/?>/g, (match, pre, href, post) => {
            const link = (href || '').trim()
            if (link) {
                return `<a href="${link}" target="_blank" rel="noopener noreferrer" style="cursor:pointer;display:inline-block;"><img${pre}${post} data-href="${link}" /></a>`
            }
            return `<img${pre} data-href="${href}"${post} />`
        })
    }, [detailHtml])

    if (!merged) return null

    const mainSrc = urls[activeIndex] || urls[0] || ''

    return (
        <div className={styles.wrap}>
            <div className={styles.topRow}>
                <div className={styles.gallery}>
                    {thumbFiles.length > 0 ? (
                        <div className={styles.thumbs}>
                            {thumbFiles.map((file, i) => (
                                <img
                                    key={`${file}-${i}`}
                                    src={toImgSrc(file)}
                                    alt=""
                                    className={`${styles.thumb} ${i === activeIndex ? styles.thumbActive : ''}`}
                                    onClick={() => setActiveIndex(i)}
                                />
                            ))}
                        </div>
                    ) : null}
                    <div>
                        {mainSrc ? (
                            <img className={styles.mainImg} src={mainSrc} alt={merged.eowg_name || ''} />
                        ) : (
                            <div
                                className={styles.mainImg}
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bfbfbf' }}
                            >
                                暂无图片
                            </div>
                        )}
                    </div>
                </div>

                <div className={styles.info}>
                    <h2 className={styles.title}>{merged.eowg_name || '商品'}</h2>
                    {merged.eowg_sub_name ? <div className={styles.subTitle}>{merged.eowg_sub_name}</div> : null}

                    <div className={styles.specLabel}>规格</div>
                    {guigeList.length > 0 ? (
                        <div className={styles.specList}>
                            {guigeList.map((g) => {
                                const active = selectedGuige?.eowgg_id === g.eowgg_id
                                return (
                                    <div
                                        key={g.eowgg_id}
                                        role="button"
                                        tabIndex={0}
                                        className={`${styles.specRow} ${active ? styles.specRowActive : ''}`}
                                        onClick={() => setSelectedGuige(g)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') setSelectedGuige(g)
                                        }}
                                    >
                                        <span className={styles.specName}>{g.eowgg_name || '规格'}</span>
                                        {g.eowgg_jiage != null && g.eowgg_jiage !== '' ? (
                                            <span className={styles.specPrice}>¥{g.eowgg_jiage}</span>
                                        ) : null}
                                    </div>
                                )
                            })}
                        </div>
                    ) : (
                        <div className={styles.subTitle} style={{ marginBottom: 16 }}>
                            暂无规格，请联系客服
                        </div>
                    )}

                    <div className={styles.orderBtnWrap}>
                        <Button
                            type="primary"
                            danger
                            size="large"
                            block
                            className={styles.orderBtn}
                            disabled={!selectedGuige}
                            onClick={() => {
                                if (!selectedGuige) {
                                    message.warning('请先选择规格')
                                    return
                                }
                                navigate('/SysHome/WebGoodsOrderAdd', {
                                    state: {
                                        fromGoodsDesc: true,
                                        eowg_id: merged.eowg_id,
                                        eowg_name: merged.eowg_name,
                                        eowgg_id: selectedGuige.eowgg_id,
                                        eowgg_name: selectedGuige.eowgg_name,
                                    },
                                })
                            }}
                        >
                            立即订购
                        </Button>
                    </div>
                </div>
            </div>

            <div className={styles.detailSection}>
                <div className={`${styles.detailHtml} detail-html`} dangerouslySetInnerHTML={{ __html: processedDetailHtml }} />
            </div>
        </div>
    )
}

export default WebGoodsSubTypeGoodsDesc
